import type {
  ChangeEventHandler,
  FocusEventHandler,
  HTMLAttributes,
} from 'react';
import type { WebsocketAwarenessState } from '../../hooks/useWebsocket/websocket';
import type { HelpErrorTextsProps } from '../HelpErrorTextsTemplate/HelpErrorTextsTemplate';

import { useCallback, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import FieldEditorsIndicator from '../FieldEditorsIndicator/FieldEditorsIndicator';
import HelpErrorTextsTemplate from '../HelpErrorTextsTemplate/HelpErrorTextsTemplate';
import RequiredTemplate from '../RequiredTemplate/RequiredTemplate';

export interface TextareaProps
  extends HTMLAttributes<HTMLTextAreaElement>, HelpErrorTextsProps {
  /**
   * Textarea value
   */
  value?: string;
  /**
   * Textarea name
   */
  name?: string;
  /**
   * Textarea label
   */
  label?: string;
  /**
   * If textarea is required we add "*" near label or inside input if there is no label.
   */
  required?: boolean;
  /**
   * If textarea is disabled
   */
  disabled?: boolean;
  /**
   * If textarea is readonly (non-interactive but without disabled classes)
   */
  readonly?: boolean;
  /**
   * Textarea placeholder text
   */
  placeholderText?: string;
  /**
   * Textarea additional classes
   */
  additionalClasses?: string;
  /**
   * Textarea additional input classes
   */
  additionalTextareaClasses?: string;
  /**
   * Textarea additional label classes
   */
  additionalTextareaLabelClasses?: string;
  /**
   * Textarea additional error classes
   */
  additionalTextareaErrorClasses?: string;
  /**
   * Editors data who are currently editing the field
   */
  editors?: WebsocketAwarenessState[];
  /**
   * Checkbox test id
   */
  testId?: string;
}

const Textarea = ({
  value = '',
  name = '',
  onChange,
  onBlur,
  onFocus,
  label = '',
  required = false,
  disabled = false,
  readonly = false,
  placeholderText = '',
  helpText = '',
  error = '',
  additionalClasses = '',
  additionalTextareaClasses = '',
  additionalTextareaLabelClasses = '',
  additionalTextareaErrorClasses = '',
  editors,
  testId = '',
  ...props
}: TextareaProps) => {
  const [currentValue, setCurrentValue] = useState(value);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  const handleChange = useCallback<ChangeEventHandler<HTMLTextAreaElement>>(
    (e) => {
      setCurrentValue(e.target.value);
      if (onChange) onChange(e);
    },
    [onChange],
  );

  const handleBlur = useCallback<FocusEventHandler<HTMLTextAreaElement>>(
    (e) => {
      if (onBlur) onBlur(e);
    },
    [onBlur],
  );

  return (
    <div
      className={twMerge('relative w-full flex flex-col', additionalClasses)}
      {...getTestProps(testId, 'container')}
    >
      {label && (
        <label
          className={twMerge(
            'text-sm text-slate-400 dark:text-slate-200 mb-1',
            additionalTextareaLabelClasses,
          )}
          {...getTestProps(testId, 'label')}
        >
          {label}
          {required && <RequiredTemplate />}
        </label>
      )}
      {!label && required && <RequiredTemplate absolute={true} />}

      <div className="w-full">
        <FieldEditorsIndicator editors={editors}>
          <textarea
            className={twMerge(
              'block resize text-base px-4 py-2.5 h-32 w-full',
              'focus:outline-hidden focus:border-blue',
              'border-slate-200 border rounded-lg',
              'placeholder:text-slate-400 placeholder:font-light',
              'text-ellipsis text-slate-900',
              'bg-white dark:bg-transparent dark:border-slate-800 dark:text-white',
              disabled && 'bg-slate-100 dark:bg-slate-900 cursor-not-allowed',
              required && !label && 'pr-5',
              error ? 'border-red' : '',
              additionalTextareaClasses,
            )}
            name={name}
            value={currentValue}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholderText}
            required={required}
            disabled={disabled}
            readOnly={readonly}
            onFocus={onFocus}
            {...props}
            {...getTestProps(testId, 'textarea')}
          />
        </FieldEditorsIndicator>
      </div>

      <HelpErrorTextsTemplate
        helpText={helpText}
        error={error}
        additionalErrorClasses={additionalTextareaErrorClasses}
        testId={testId}
      />
    </div>
  );
};

export default Textarea;
